import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface LayerProps {
  children: React.ReactNode;
}

/**
 * Draws its children in a layer appended to `document.body`, so no stacking context of the page can paint
 * over them. The console's `<main>` is one (z-index 10), which put dialogs under the fixed top bar.
 *
 * The tokens live on `:root` and `[data-theme]`, so the layer repeats the theme of the place it is drawn from:
 * the legacy views render in Dim and their dialogs have to follow. The theme is read again on every render,
 * because the shell changes it when the route changes.
 */
const Layer: React.FC<LayerProps> = ({ children }) => {
  const anchor = useRef<HTMLSpanElement>(null);
  const [host, setHost] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const el = document.createElement('div');
    el.className = 'ui-layer';
    document.body.appendChild(el);
    setHost(el);
    return () => {
      el.remove();
      setHost(null);
    };
  }, []);

  useLayoutEffect(() => {
    if (!host) return;
    const theme = anchor.current?.closest('[data-theme]')?.getAttribute('data-theme');
    if (theme) host.setAttribute('data-theme', theme);
    else host.removeAttribute('data-theme');
  });

  return (
    <>
      <span ref={anchor} hidden />
      {host && createPortal(children, host)}
    </>
  );
};

export default Layer;
